<script setup lang="ts">
import { computed, type Component, type CSSProperties } from 'vue'

import { useOverlayLayer, type OverlayLayerKind } from './overlayStack'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    kind: OverlayLayerKind
    active?: boolean
    as?: string | Component
  }>(),
  { active: true, as: 'div' },
)

const element = defineModel<HTMLElement | null>('element', { default: null })
const active = computed(() => props.active)
const { id, order, isTopmostBlocking } = useOverlayLayer(props.kind, active)
const layerStyle = computed<CSSProperties>(() => ({
  // Before the stack assigns an order (order === -1, i.e. the first frame of an opening layer)
  // still paint at the overlay base so the dialog root already owns a stacking context above
  // `--ui-z-sticky`. Falling back to `undefined` left `.ui-drawer`/`.ui-modal` at `z-index: auto`,
  // which let sticky page chrome paint over the opening overlay and flipped the root from
  // `auto` → overlay z one frame in — the stacking-context change interrupted Headless UI's
  // in-flight enter transition, freezing the panel off-screen and the backdrop mid-fade.
  zIndex:
    order.value < 0
      ? 'var(--ui-z-overlay-base)'
      : `calc(var(--ui-z-overlay-base) + ${order.value + 1})`,
}))
const layerAttributes = computed(() => ({
  'data-overlay-kind': props.kind,
  'data-overlay-order': String(order.value),
}))

function setElement(value: unknown): void {
  element.value = value instanceof HTMLElement ? value : null
}
</script>

<template>
  <slot
    v-if="as === 'template'"
    :id="id"
    :style="layerStyle"
    :order="order"
    :is-topmost-blocking="isTopmostBlocking"
    :layer-attributes="layerAttributes"
  />
  <component
    :is="as"
    v-else
    :ref="setElement"
    v-bind="{ ...$attrs, ...layerAttributes }"
    :style="[$attrs.style as CSSProperties, layerStyle]"
  >
    <slot
      :id="id"
      :style="layerStyle"
      :order="order"
      :is-topmost-blocking="isTopmostBlocking"
      :layer-attributes="layerAttributes"
    />
  </component>
</template>
